import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Building2, Calendar, TrendingUp, UserCheck, Clock, AlertTriangle, Banknote } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { superuserService } from '../../services/api';

interface DashboardStats {
  total_gerants: number;
  total_locataires: number;
  pending_gerants: number;
  total_apartments: number;
  active_apartments: number;
  total_bookings: number;
  total_revenue: number;
  this_month_revenue: number;
  new_users_this_month: number;
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await superuserService.getDashboard();
        if (response.data?.stats) {
          setStats(response.data.stats);
        }
      } catch (error: any) {
        console.error('Erreur chargement dashboard:', error.response?.data || error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-neutral-900">Tableau de bord Super Admin</h1>
        <p className="text-neutral-600">Bienvenue, {user?.prenom} ! Vue globale de la plateforme.</p>
      </div>

      {/* Alerte gérants en attente */}
      {stats && stats.pending_gerants > 0 && (
        <Link to="/admin/gerants?status=pending_admin_approval" className="block bg-orange-50 border-2 border-orange-300 rounded-xl p-4 hover:bg-orange-100 transition">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-orange-800 text-lg">
                {stats.pending_gerants} inscription{stats.pending_gerants > 1 ? 's' : ''} de gérant en attente
              </p>
              <p className="text-orange-700 text-sm">Cliquez pour valider les inscriptions</p>
            </div>
            <UserCheck className="w-8 h-8 text-orange-500" />
          </div>
        </Link>
      )}

      {/* Stats principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/admin/gerants" className="card p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500">Gérants</p>
              <p className="text-2xl font-bold">{stats?.total_gerants || 0}</p>
            </div>
            <Users className="w-10 h-10 text-primary-500 opacity-80" />
          </div>
        </Link>

        <Link to="/admin/locataires" className="card p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500">Locataires</p>
              <p className="text-2xl font-bold">{stats?.total_locataires || 0}</p>
            </div>
            <Users className="w-10 h-10 text-green-500 opacity-80" />
          </div>
        </Link>

        <Link to="/admin/gerants?status=pending_admin_approval" className="card p-6 hover:shadow-lg transition-shadow border-2 border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-medium">⏳ En attente</p>
              <p className="text-2xl font-bold text-orange-600">{stats?.pending_gerants || 0}</p>
            </div>
            <Clock className="w-10 h-10 text-orange-500 opacity-80" />
          </div>
        </Link>

        <Link to="/admin/apartments" className="card p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500">Appartements</p>
              <p className="text-2xl font-bold">{stats?.total_apartments || 0}</p>
            </div>
            <Building2 className="w-10 h-10 text-blue-500 opacity-80" />
          </div>
        </Link>
      </div>

      {/* Stats secondaires */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/admin/bookings" className="card p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500">Total Réservations</p>
              <p className="text-2xl font-bold">{stats?.total_bookings || 0}</p>
            </div>
            <Calendar className="w-10 h-10 text-purple-500 opacity-80" />
          </div>
        </Link>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500">Revenus Totaux</p>
              <p className="text-xl font-bold">{formatCurrency(stats?.total_revenue || 0)}</p>
            </div>
            <TrendingUp className="w-10 h-10 text-green-500 opacity-80" />
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500">Revenus ce mois</p>
              <p className="text-xl font-bold">{formatCurrency(stats?.this_month_revenue || 0)}</p>
            </div>
            <Banknote className="w-10 h-10 text-emerald-500 opacity-80" />
          </div>
        </div>
      </div>

      {/* Actions rapides */}
      <div className="card p-6">
        <h2 className="font-semibold mb-4">Actions rapides</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Link to="/admin/gerants" className="btn btn-primary">
            Gérer les gérants
          </Link>
          <Link to="/admin/locataires" className="btn btn-outline">
            Voir les locataires
          </Link>
          <Link to="/admin/apartments" className="btn btn-outline">
            Voir les appartements
          </Link>
          <Link to="/admin/bookings" className="btn btn-outline">
            Voir les réservations
          </Link>
          <Link to="/admin/gerants?status=pending_admin_approval" className="btn btn-outline border-orange-300 text-orange-600 hover:bg-orange-50">
            Inscriptions en attente
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
